import { getLocale, t } from './i18n.mjs?v=20260806.100';
import {
  kpiSparklineMarkup,
  mountKpiSparklines,
} from './kpi-sparkline.mjs?v=20260806.100';

const TEAM_ROLES = new Set(['dispatcher', 'clerk', 'finance']);
const ROLE_ORDER = Object.freeze(['dispatcher', 'clerk', 'finance', 'driver']);
const DRIVER_RANKING_EXAMPLES = Object.freeze([
  Object.freeze({ name: 'Alex Rivera', deliveries: 12 }),
  Object.freeze({ name: 'David Kim', deliveries: 10 }),
  Object.freeze({ name: 'Ethan Nguyen', deliveries: 8 }),
  Object.freeze({ name: 'Jordan Lee', deliveries: 7 }),
  Object.freeze({ name: 'Maria Garcia', deliveries: 5 }),
]);
const ROLE_META = Object.freeze({
  admin: Object.freeze({
    labelKey: 'team.role.admin',
    departmentKey: 'team.department.administration',
    icon: 'ph-shield-check',
    tone: 'indigo',
  }),
  dispatcher: Object.freeze({
    labelKey: 'team.role.dispatcher',
    departmentKey: 'team.department.dispatch',
    icon: 'ph-truck',
    tone: 'blue',
  }),
  clerk: Object.freeze({
    labelKey: 'team.role.clerk',
    departmentKey: 'team.department.operations',
    icon: 'ph-clipboard-text',
    tone: 'purple',
  }),
  finance: Object.freeze({
    labelKey: 'team.role.finance',
    departmentKey: 'team.department.finance',
    icon: 'ph-currency-dollar',
    tone: 'orange',
  }),
  driver: Object.freeze({
    labelKey: 'team.role.driver',
    departmentKey: 'team.department.operations',
    icon: 'ph-steering-wheel',
    tone: 'green',
  }),
});

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function timestampMillis(value) {
  if (!value) return null;
  if (Number.isFinite(value)) return Number(value);
  if (value instanceof Date) return Number.isFinite(value.valueOf()) ? value.valueOf() : null;
  if (typeof value.toMillis === 'function') {
    const millis = value.toMillis();
    return Number.isFinite(millis) ? millis : null;
  }
  if (typeof value.toDate === 'function') {
    const date = value.toDate();
    return date instanceof Date && Number.isFinite(date.valueOf()) ? date.valueOf() : null;
  }
  if (Number.isFinite(value.seconds)) return Number(value.seconds) * 1000;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : null;
}

function dateKey(value, timeZone = 'America/Los_Angeles') {
  const millis = timestampMillis(value);
  if (!Number.isFinite(millis)) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(millis));
  const lookup = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${lookup.year}-${lookup.month}-${lookup.day}`;
}

function normalizeIdentity(value) {
  return String(value ?? '').trim().normalize('NFKC').toLocaleLowerCase('en-US');
}

function initials(name) {
  const parts = String(name ?? '').trim().split(/\s+/u).filter(Boolean);
  if (!parts.length) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts.at(-1)[0]}`.toUpperCase();
}

function roleMeta(role) {
  return ROLE_META[role] || ROLE_META.clerk;
}

function todoRankingCopy(locale) {
  if (String(locale || '').toLowerCase().startsWith('zh')) {
    return Object.freeze({
      title: 'Todo 完成排行榜',
      completed: '已完成',
      emptyTitle: '暂无 Todo 完成记录',
      emptyDetail: '后端提供员工永久完成总量后，排名将在此显示。',
    });
  }
  return Object.freeze({
    title: 'Todo Completion Ranking',
    completed: 'Completed',
    emptyTitle: 'No Todo completion records available',
    emptyDetail: 'Permanent totals will appear when backend worker records are available.',
  });
}

function onLeave(record) {
  return record?.onLeave === true
    || record?.on_leave === true
    || ['on_leave', 'leave'].includes(String(record?.status || record?.current_status || '').toLowerCase());
}

export function attendanceEmployeeStatus(
  employee,
  todayKey,
  timeZone = 'America/Los_Angeles',
  locale = getLocale(),
) {
  if (onLeave(employee)) {
    return Object.freeze({ key: 'on-leave', label: t('team.status.onLeave', {}, locale) });
  }
  if (!employee?.active || String(employee?.current_status || '').toLowerCase() === 'inactive') {
    return Object.freeze({ key: 'inactive', label: t('team.status.inactive', {}, locale) });
  }
  if (dateKey(employee.lastClockedAt ?? employee.last_clocked_at, timeZone) === todayKey) {
    return Object.freeze({ key: 'active', label: t('team.status.active', {}, locale) });
  }
  return Object.freeze({ key: 'offline', label: t('team.status.offline', {}, locale) });
}

function attendanceLookup(overview = {}) {
  const employees = Array.isArray(overview.employees) ? overview.employees : [];
  const byName = new Map();
  const byEmail = new Map();
  employees.forEach((employee) => {
    const name = normalizeIdentity(employee.loginName ?? employee.login_name ?? employee.displayName);
    const email = normalizeIdentity(employee.email);
    if (name && !byName.has(name)) byName.set(name, employee);
    if (email && !byEmail.has(email)) byEmail.set(email, employee);
  });
  return { byName, byEmail };
}

function matchedAttendance(record, lookup) {
  const name = normalizeIdentity(
    record.display_name ?? record.full_name ?? record.loginName ?? record.name,
  );
  const email = normalizeIdentity(record.email);
  return lookup.byName.get(name) || lookup.byEmail.get(email) || null;
}

function memberStatus(record, attendance, todayKey, timeZone, locale) {
  if (onLeave(record) || onLeave(attendance)) {
    return Object.freeze({ key: 'on-leave', label: t('team.status.onLeave', {}, locale) });
  }
  if (record.active === false || String(record.current_status || '').toLowerCase() === 'inactive') {
    return Object.freeze({ key: 'inactive', label: t('team.status.inactive', {}, locale) });
  }
  const activeDriver = ['assigned', 'on_route'].includes(String(record.current_status || '').toLowerCase());
  const lastActivity = attendance?.lastClockedAt
    ?? attendance?.last_clocked_at
    ?? record.last_active_at
    ?? record.last_login_at
    ?? record.last_location_at;
  if (activeDriver || dateKey(lastActivity, timeZone) === todayKey) {
    return Object.freeze({ key: 'active', label: t('team.status.active', {}, locale) });
  }
  return Object.freeze({ key: 'offline', label: t('team.status.offline', {}, locale) });
}

function memberRecord(record, source, overview, todayKey, timeZone, locale) {
  const lookup = attendanceLookup(overview);
  const attendance = matchedAttendance(record, lookup);
  const role = source === 'driver' ? 'driver' : String(record.role || '').toLowerCase();
  const meta = roleMeta(role);
  const name = String(
    record.display_name
    ?? record.full_name
    ?? record.email
    ?? record.id
    ?? t('team.unnamedMember', {}, locale),
  ).trim();
  const lastActiveAt = attendance?.lastClockedAt
    ?? attendance?.last_clocked_at
    ?? record.last_active_at
    ?? record.last_login_at
    ?? record.last_location_at
    ?? null;
  const status = memberStatus(record, attendance, todayKey, timeZone, locale);
  const currentDriverStatus = String(record.current_status || '').toLowerCase();
  return Object.freeze({
    id: String(record.id || ''),
    name,
    initials: initials(name),
    employeeId: String(record.employee_id || record.driver_code || record.id || '—'),
    role: t(meta.labelKey, {}, locale),
    roleKey: role,
    roleIcon: meta.icon,
    roleTone: meta.tone,
    department: t(meta.departmentKey, {}, locale),
    status,
    lastActiveAt,
    activeToday: dateKey(lastActiveAt, timeZone) === todayKey,
    onDuty: status.key === 'active'
      && (source !== 'driver' || ['assigned', 'on_route'].includes(currentDriverStatus)),
    attendanceEmployeeId: String(attendance?.id || ''),
    attendanceActive: attendance?.active !== false,
  });
}

export function buildTeamMembers({
  overview = {},
  users = [],
  drivers = [],
  todayKey,
  timeZone = overview?.office?.timezone || 'America/Los_Angeles',
  locale = getLocale(),
} = {}) {
  const staff = (Array.isArray(users) ? users : [])
    .filter((user) => (
      TEAM_ROLES.has(String(user?.role || '').toLowerCase())
      && user?.archived_by_restore !== true
    ))
    .map((user) => memberRecord(user, 'staff', overview, todayKey, timeZone, locale));
  const operationalDrivers = (Array.isArray(drivers) ? drivers : [])
    .filter((driver) => driver?.archived_by_restore !== true)
    .map((driver) => memberRecord(driver, 'driver', overview, todayKey, timeZone, locale));
  return Object.freeze([...staff, ...operationalDrivers].sort((a, b) => {
    const roleDifference = ROLE_ORDER.indexOf(a.roleKey) - ROLE_ORDER.indexOf(b.roleKey);
    if (roleDifference) return roleDifference;
    return a.name.localeCompare(b.name);
  }));
}

/**
 * Backend contract: one lifetime total per
 * { worker_account: string, role: 'dispatcher'|'clerk'|'finance', count: non-negative integer }.
 */
export function buildTodoCompletionRanking(records = []) {
  const ranking = (Array.isArray(records) ? records : []).flatMap((record) => {
    const workerAccount = String(record?.worker_account ?? '').trim();
    const role = String(record?.role ?? '').trim().toLowerCase();
    const count = record?.count;
    if (!workerAccount || !TEAM_ROLES.has(role) || !Number.isInteger(count) || count < 0) {
      return [];
    }
    return [Object.freeze({ workerAccount, role, count })];
  });
  ranking.sort((a, b) => (
    b.count - a.count
    || a.workerAccount.localeCompare(b.workerAccount)
  ));
  return Object.freeze(ranking);
}

function lastSevenDateKeys(todayKey) {
  const anchor = new Date(`${todayKey}T12:00:00.000Z`);
  if (!Number.isFinite(anchor.valueOf())) return Object.freeze(Array(7).fill(todayKey || ''));
  return Object.freeze(Array.from({ length: 7 }, (_, index) => {
    const date = new Date(anchor);
    date.setUTCDate(anchor.getUTCDate() - (6 - index));
    return date.toISOString().slice(0, 10);
  }));
}

function hasClerkDocument(row) {
  const fields = row?.field_values || {};
  return Boolean(
    fields.pre_alert_status
    || fields.forecast_status
    || row?.pre_alert_status
    || row?.forecast_status
    || row?.document_ready,
  );
}

function hasFinanceWork(row) {
  const finance = row?.finance_summary || {};
  return Boolean(
    finance.ctf_status
    || finance.tmf_status
    || finance.customer_paid === true
    || finance.remittance_status
    || row?.ctf_status
    || row?.tmf_status
    || row?.customer_paid === true,
  );
}

function incrementSeries(series, keys, key) {
  const index = keys.indexOf(key);
  if (index >= 0) series[index] += 1;
}

export function teamOperationalSnapshot({
  rows = [],
  todayKey,
  timeZone = 'America/Los_Angeles',
} = {}) {
  const keys = lastSevenDateKeys(todayKey);
  const dispatcher = Array(7).fill(0);
  const clerk = Array(7).fill(0);
  const finance = Array(7).fill(0);
  const completedByDriver = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const task = row?.dispatch_task || {};
    const assignmentKey = dateKey(
      task.scheduled_start_at_millis ?? row?.pickup_appointment_at_millis,
      timeZone,
    );
    if (assignmentKey) incrementSeries(dispatcher, keys, assignmentKey);

    const updatedKey = dateKey(row?.updated_at_millis ?? row?.updated_at, timeZone);
    if (updatedKey && hasClerkDocument(row)) incrementSeries(clerk, keys, updatedKey);
    if (updatedKey && hasFinanceWork(row)) incrementSeries(finance, keys, updatedKey);

    const completedKey = dateKey(
      row?.empty_returned_at_millis ?? row?.actual_empty_returned_at_millis,
      timeZone,
    );
    if (completedKey !== todayKey) return;
    const driverName = String(
      row?.assigned_driver_name
      || task.driver_name
      || row?.pickup_driver_name
      || '',
    ).trim();
    if (!driverName) return;
    completedByDriver.set(driverName, (completedByDriver.get(driverName) || 0) + 1);
  });

  const driverRanking = [...completedByDriver.entries()]
    .map(([name, deliveries]) => Object.freeze({ name, deliveries }))
    .sort((a, b) => b.deliveries - a.deliveries || a.name.localeCompare(b.name))
    .slice(0, 5);
  return Object.freeze({
    labels: keys,
    dispatcher: Object.freeze({ value: dispatcher.at(-1) || 0, series: Object.freeze(dispatcher) }),
    clerk: Object.freeze({ value: clerk.at(-1) || 0, series: Object.freeze(clerk) }),
    finance: Object.freeze({ value: finance.at(-1) || 0, series: Object.freeze(finance) }),
    driverRanking: Object.freeze(driverRanking),
  });
}

function normalizedSeries(value, current) {
  if (!Array.isArray(value) || value.length !== 7) return Object.freeze(Array(7).fill(current));
  return Object.freeze(value.map((item) => Math.max(0, Number(item) || 0)));
}

function metricTrend(series, explicit) {
  if (explicit === null || explicit === undefined) {
    return Object.freeze({ direction: 'unavailable', value: '—' });
  }
  const delta = Number(explicit);
  if (!Number.isFinite(delta)) return Object.freeze({ direction: 'unavailable', value: '—' });
  return Object.freeze({
    direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat',
    value: `${Math.abs(Math.round(delta))}`,
  });
}

function metricCard({
  kind,
  icon,
  title,
  value,
  series,
  trend,
  labels,
  locale,
}) {
  const trendLabel = trend.direction === 'unavailable'
    ? t('team.trendUnavailable', {}, locale)
    : `${trend.direction === 'down' ? '↓' : trend.direction === 'up' ? '↑' : '•'} ${trend.value}`;
  return `<article class="team-kpi-card team-kpi-card--${escapeHtml(kind)}">
    <span class="team-kpi-card__icon" aria-hidden="true"><i class="ph ${escapeHtml(icon)}"></i></span>
    <div class="team-kpi-card__body">
      <span class="team-kpi-card__title">${escapeHtml(title)}</span>
      <strong>${Number(value || 0).toLocaleString(locale)}</strong>
      <span class="team-kpi-card__trend team-kpi-card__trend--${escapeHtml(trend.direction)}">
        <small>${escapeHtml(t('team.vsLast7Days', {}, locale))}</small>
        <b>${escapeHtml(trendLabel)}</b>
      </span>
    </div>
    <div class="team-kpi-card__spark">${kpiSparklineMarkup({
      values: series,
      labels,
      ariaLabel: `${title} ${t('team.sevenDayTrend', {}, locale)}`,
    })}</div>
  </article>`;
}

function relativeLastActive(value, locale, nowMillis = Date.now()) {
  const millis = timestampMillis(value);
  if (!Number.isFinite(millis)) return '—';
  const seconds = Math.max(0, Math.round((nowMillis - millis) / 1000));
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (seconds < 60) return formatter.format(-Math.max(1, seconds), 'second');
  if (seconds < 3600) return formatter.format(-Math.round(seconds / 60), 'minute');
  if (seconds < 86400) return formatter.format(-Math.round(seconds / 3600), 'hour');
  if (seconds < 172800) return formatter.format(-1, 'day');
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(new Date(millis));
}

function driverMatches(driver, value) {
  const identity = normalizeIdentity(value?.id ?? value?.path ?? value);
  if (!identity) return false;
  const candidates = [
    driver?.id,
    driver?.driver_code,
    driver?.full_name,
    driver?.display_name,
    driver?.email,
    `drivers/${driver?.id || ''}`,
  ].map(normalizeIdentity).filter(Boolean);
  return candidates.some((candidate) => identity === candidate || identity.endsWith(`/${candidate}`));
}

function rowValue(row, key) {
  if (row?.[key] != null) return row[key];
  if (row?.field_values?.[key] != null) return row.field_values[key];
  if (row?.finance_summary?.[key] != null) return row.finance_summary[key];
  return null;
}

function completedContainer(row) {
  const status = normalizeIdentity(rowValue(row, 'status') || rowValue(row, 'ocean_status'));
  return row?.canonical_completed === true
    || row?.is_empty_returned === true
    || Number.isFinite(timestampMillis(
      rowValue(row, 'empty_returned_at_millis') || rowValue(row, 'empty_returned_at'),
    ))
    || ['empty_returned', 'empty_returned_to_terminal', 'completed'].includes(status);
}

function driverParticipated(driver, row) {
  return [
    rowValue(row, 'assigned_driver_ref'),
    rowValue(row, 'assigned_driver_uid'),
    rowValue(row, 'assigned_driver_name'),
    rowValue(row, 'pickup_driver_ref'),
    rowValue(row, 'pickup_driver_uid'),
    rowValue(row, 'pickup_driver_name'),
    rowValue(row, 'return_driver_ref'),
    rowValue(row, 'return_driver_uid'),
    rowValue(row, 'return_driver_name'),
    rowValue(row, 'transfer_driver_ref'),
    rowValue(row, 'transfer_driver_uid'),
    rowValue(row, 'transfer_driver_name'),
  ].some((value) => driverMatches(driver, value));
}

function monthKey(value, timeZone = 'America/Los_Angeles') {
  const key = dateKey(value, timeZone);
  return key ? key.slice(0, 7) : '';
}

function driverLegEarnings(driver, row) {
  const legs = [
    {
      name: rowValue(row, 'pickup_driver_name') || rowValue(row, 'assigned_driver_name'),
      ref: rowValue(row, 'pickup_driver_ref') || rowValue(row, 'assigned_driver_ref'),
      amount: Number(rowValue(row, 'pickup_cost_cents')) || 0,
      at: rowValue(row, 'load_out_at_millis') || rowValue(row, 'pickup_appointment_at_millis')
        || rowValue(row, 'load_out_at') || rowValue(row, 'pickup_appointment_at')
        || rowValue(row, 'updated_at_millis') || rowValue(row, 'updated_at'),
    },
    {
      name: rowValue(row, 'return_driver_name'),
      ref: rowValue(row, 'return_driver_ref'),
      amount: Number(rowValue(row, 'return_cost_cents')) || 0,
      at: rowValue(row, 'empty_returned_at_millis') || rowValue(row, 'actual_empty_returned_at_millis')
        || rowValue(row, 'empty_returned_at') || rowValue(row, 'actual_empty_returned_at')
        || rowValue(row, 'updated_at_millis') || rowValue(row, 'updated_at'),
    },
    {
      name: rowValue(row, 'transfer_driver_name'),
      ref: rowValue(row, 'transfer_driver_ref'),
      amount: Number(rowValue(row, 'transfer_cost_cents')) || 0,
      at: rowValue(row, 'transfer_at_millis') || rowValue(row, 'transfer_at')
        || rowValue(row, 'updated_at_millis') || rowValue(row, 'updated_at'),
    },
  ];
  return legs.filter((leg) => leg.amount > 0 && (
    driverMatches(driver, leg.name) || driverMatches(driver, leg.ref)
  ));
}

function formatMoney(cents, locale = 'en-US') {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format((Number(cents) || 0) / 100);
}

export function driverProfileSnapshot({
  driver = {},
  rows = [],
  now = new Date(),
  timeZone = 'America/Los_Angeles',
} = {}) {
  const safeNow = new Date(now);
  const year = Number.isFinite(safeNow.valueOf()) ? safeNow.getFullYear() : new Date().getFullYear();
  const currentMonth = monthKey(safeNow, timeZone);
  const completedTasks = (Array.isArray(rows) ? rows : [])
    .filter((row) => completedContainer(row) && driverParticipated(driver, row))
    .map((row) => {
      const completedAt = rowValue(row, 'empty_returned_at_millis')
        || rowValue(row, 'actual_empty_returned_at_millis')
        || rowValue(row, 'empty_returned_at')
        || rowValue(row, 'actual_empty_returned_at')
        || rowValue(row, 'updated_at_millis')
        || rowValue(row, 'updated_at');
      const deadline = rowValue(row, 'return_empty_lfd_at_millis')
        || rowValue(row, 'return_empty_lfd_at');
      const completedMillis = timestampMillis(completedAt);
      const deadlineMillis = timestampMillis(deadline);
      return Object.freeze({
        id: String(row.id || ''),
        containerNo: String(rowValue(row, 'container_no') || row.id || '—'),
        pickup: String(rowValue(row, 'terminal_name') || rowValue(row, 'pickup_company') || '—'),
        delivery: String(rowValue(row, 'delivery_warehouse_address') || '—'),
        completedAt,
        completedMillis,
        onTime: Number.isFinite(deadlineMillis) && Number.isFinite(completedMillis)
          ? completedMillis <= deadlineMillis : null,
      });
    })
    .sort((a, b) => (b.completedMillis || 0) - (a.completedMillis || 0));

  const eligibleOnTime = completedTasks.filter((task) => task.onTime !== null);
  const onTimeCount = eligibleOnTime.filter((task) => task.onTime).length;
  const monthlyEarnings = Array.from({ length: 12 }, (_, index) => ({
    key: `${year}-${String(index + 1).padStart(2, '0')}`,
    value: 0,
  }));
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    driverLegEarnings(driver, row).forEach((leg) => {
      const key = monthKey(leg.at, timeZone);
      const month = monthlyEarnings.find((item) => item.key === key);
      if (month) month.value += leg.amount;
    });
  });
  const monthEarningsCents = monthlyEarnings.find((item) => item.key === currentMonth)?.value || 0;
  const yearEarningsCents = monthlyEarnings.reduce((sum, item) => sum + item.value, 0);
  const name = String(driver.full_name || driver.display_name || driver.name || driver.id || 'Driver');
  const currentStatus = String(driver.current_status || '').toLowerCase();
  return Object.freeze({
    id: String(driver.id || ''),
    name,
    initials: initials(name),
    employeeId: String(driver.driver_code || driver.employee_id || driver.id || '—'),
    driverCode: String(driver.driver_code || driver.employee_id || driver.id || ''),
    phone: String(driver.phone || '—'),
    phoneValue: String(driver.phone || ''),
    email: String(driver.real_email || driver.email || '—'),
    emailValue: String(driver.real_email || driver.email || ''),
    wechat: String(driver.wechat || ''),
    licenseNo: String(driver.license_no || '—'),
    licenseNoValue: String(driver.license_no || ''),
    truckNo: String(driver.tractor_no || driver.truck_no || '—'),
    truckNoValue: String(driver.tractor_no || driver.truck_no || ''),
    trailerNo: String(driver.trailer_no || ''),
    notes: String(driver.notes || ''),
    currentStatus: ['available', 'assigned', 'on_route', 'off_duty', 'inactive'].includes(currentStatus)
      ? currentStatus : 'available',
    status: ['assigned', 'on_route'].includes(currentStatus)
      ? 'On Duty' : currentStatus === 'inactive' || driver.active === false ? 'Inactive' : 'Available',
    completedContainers: completedTasks.length,
    onTimeRate: eligibleOnTime.length ? Math.round((onTimeCount / eligibleOnTime.length) * 100) : null,
    lateDeliveries: eligibleOnTime.length - onTimeCount,
    monthEarningsCents,
    yearEarningsCents,
    monthlyEarnings: Object.freeze(monthlyEarnings.map((item) => Object.freeze(item))),
    completedTasks: Object.freeze(completedTasks),
  });
}

function completedTaskRows(profile, locale) {
  const dateFormatter = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    // 24-hour everywhere (Alan, 2026-08-11).
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const rows = profile.completedTasks.slice(0, 25);
  if (!rows.length) {
    return '<tr><td colspan="5" class="team-driver-drawer__empty">No completed containers recorded.</td></tr>';
  }
  return rows.map((task) => `<tr>
    <td><strong>${escapeHtml(task.containerNo)}</strong></td>
    <td>${escapeHtml(task.pickup)}</td>
    <td>${escapeHtml(task.delivery)}</td>
    <td>${escapeHtml(Number.isFinite(task.completedMillis) ? dateFormatter.format(new Date(task.completedMillis)) : '—')}</td>
    <td><span class="team-driver-drawer__complete">Completed</span></td>
  </tr>`).join('');
}

export function buildDriverProfileUpdatePayload(values, driverId) {
  const source = values && typeof values === 'object' ? values : {};
  const text = (key) => String(source[key] ?? '').trim();
  const currentStatus = text('current_status');
  if (!['available', 'assigned', 'on_route', 'off_duty', 'inactive'].includes(currentStatus)) {
    throw new Error('Select a valid driver status.');
  }
  const fullName = text('full_name');
  const driverCode = text('driver_code');
  if (!fullName || !driverCode || !String(driverId || '').trim()) {
    throw new Error('Driver name and ID are required.');
  }
  const realEmail = text('real_email').toLowerCase();
  if (realEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(realEmail)) {
    throw new Error('Enter a valid driver email.');
  }
  return Object.freeze({
    driver_id: String(driverId).trim(),
    full_name: fullName,
    driver_code: driverCode,
    phone: text('phone'),
    real_email: realEmail,
    wechat: text('wechat'),
    truck_no: text('truck_no'),
    trailer_no: text('trailer_no'),
    license_no: text('license_no'),
    current_status: currentStatus,
    notes: text('notes'),
  });
}

function driverEditForm(profile) {
  const statusOptions = [
    ['available', 'Available'],
    ['assigned', 'Assigned'],
    ['on_route', 'On Route'],
    ['off_duty', 'Off Duty'],
    ['inactive', 'Inactive'],
  ].map(([value, label]) => `<option value="${value}"${profile.currentStatus === value ? ' selected' : ''}>${label}</option>`).join('');
  return `<form class="team-driver-edit-form" data-team-driver-edit-form data-driver-id="${escapeHtml(profile.id)}" hidden>
    <div class="team-driver-edit-form__heading">
      <div><span>Admin edit</span><h3>Driver Information</h3></div>
      <p>Changes save directly to the Driver business record.</p>
    </div>
    <div class="team-driver-edit-grid">
      <label><span>Name</span><input name="full_name" value="${escapeHtml(profile.name)}" maxlength="200" required></label>
      <label><span>Driver ID</span><input name="driver_code" value="${escapeHtml(profile.driverCode)}" maxlength="100" required></label>
      <label><span>Phone</span><input name="phone" value="${escapeHtml(profile.phoneValue)}" maxlength="80" inputmode="tel"></label>
      <label><span>Real Email</span><input name="real_email" value="${escapeHtml(profile.emailValue)}" maxlength="254" type="email"></label>
      <label><span>WeChat</span><input name="wechat" value="${escapeHtml(profile.wechat)}" maxlength="100"></label>
      <label><span>Truck No.</span><input name="truck_no" value="${escapeHtml(profile.truckNoValue)}" maxlength="100"></label>
      <label><span>Trailer No.</span><input name="trailer_no" value="${escapeHtml(profile.trailerNo)}" maxlength="100"></label>
      <label><span>License No.</span><input name="license_no" value="${escapeHtml(profile.licenseNoValue)}" maxlength="100"></label>
      <label><span>Status</span><select name="current_status">${statusOptions}</select></label>
      <label class="team-driver-edit-grid__wide"><span>Notes</span><textarea name="notes" maxlength="1000">${escapeHtml(profile.notes)}</textarea></label>
    </div>
    <p class="team-driver-edit-form__error" data-team-driver-edit-error aria-live="polite"></p>
    <div class="team-driver-edit-form__actions">
      <button type="button" data-team-driver-edit-cancel>Cancel</button>
      <button type="submit"><i class="ph ph-check" aria-hidden="true"></i>Save changes</button>
    </div>
  </form>`;
}

export function teamDriverProfileMarkup(profile, locale = getLocale(), { canEdit = false } = {}) {
  if (!profile) return '';
  const months = profile.monthlyEarnings.map((item) => new Intl.DateTimeFormat(locale, {
    month: 'short',
  }).format(new Date(`${item.key}-15T12:00:00Z`)));
  const maxEarnings = Math.max(1, ...profile.monthlyEarnings.map((item) => item.value));
  const onTime = profile.onTimeRate == null ? '—' : `${profile.onTimeRate}%`;
  return `<section class="team-driver-drawer" data-team-driver-drawer role="dialog" aria-modal="true" aria-labelledby="teamDriverTitle-${escapeHtml(profile.id)}">
    <button type="button" class="team-driver-drawer__close" data-team-driver-close aria-label="Close driver details"><i class="ph ph-x" aria-hidden="true"></i></button>
    <header class="team-driver-drawer__hero">
      <span class="team-driver-drawer__avatar" aria-hidden="true">${escapeHtml(profile.initials)}</span>
      <div><span class="team-driver-drawer__status">${escapeHtml(profile.status)}</span><div class="team-driver-drawer__title"><h2 id="teamDriverTitle-${escapeHtml(profile.id)}">${escapeHtml(profile.name)}</h2>${canEdit ? `<button type="button" data-team-driver-edit-toggle aria-expanded="false" aria-label="Edit ${escapeHtml(profile.name)} driver information"><i class="ph ph-pencil-simple" aria-hidden="true"></i></button>` : ''}</div><p><b>Driver</b><span>${escapeHtml(profile.employeeId)}</span></p></div>
    </header>
    <div class="team-driver-drawer__contact">
      <span><i class="ph ph-phone" aria-hidden="true"></i>${escapeHtml(profile.phone)}</span>
      <span><i class="ph ph-truck" aria-hidden="true"></i>${escapeHtml(profile.truckNo)}</span>
      <span><i class="ph ph-identification-card" aria-hidden="true"></i>${escapeHtml(profile.licenseNo)}</span>
    </div>
    <div class="team-driver-drawer__body">
      ${canEdit ? driverEditForm(profile) : ''}
      <section class="team-driver-section team-driver-section--basic">
        <h3>Basic Information</h3>
        <dl class="team-driver-basic">
          <div><dt>Employee ID</dt><dd>${escapeHtml(profile.employeeId)}</dd></div>
          <div><dt>Truck No.</dt><dd>${escapeHtml(profile.truckNo)}</dd></div>
          <div><dt>License No.</dt><dd>${escapeHtml(profile.licenseNo)}</dd></div>
          <div><dt>Phone</dt><dd>${escapeHtml(profile.phone)}</dd></div>
          <div><dt>Email</dt><dd>${escapeHtml(profile.email)}</dd></div>
          <div><dt>Status</dt><dd>${escapeHtml(profile.status)}</dd></div>
        </dl>
      </section>
      <section class="team-driver-section team-driver-section--performance">
        <h3>Performance Summary</h3>
        <div class="team-driver-summary">
          <article><strong>${profile.completedContainers.toLocaleString(locale)}</strong><span>Completed Containers</span></article>
          <article><strong>${escapeHtml(onTime)}</strong><span>On-Time Rate</span></article>
          <article><strong>${profile.lateDeliveries.toLocaleString(locale)}</strong><span>Late Deliveries</span></article>
          <article><strong>${escapeHtml(formatMoney(profile.monthEarningsCents, locale))}</strong><span>This Month Earnings</span></article>
        </div>
      </section>
      <section class="team-driver-section team-driver-section--tasks">
        <div class="team-driver-section-heading"><h3>Completed Tasks</h3><strong>${profile.completedContainers.toLocaleString(locale)} total</strong></div>
        <div class="team-driver-task-wrap"><table><thead><tr><th>Container #</th><th>Pickup</th><th>Delivery</th><th>Completed</th><th>Status</th></tr></thead><tbody>${completedTaskRows(profile, locale)}</tbody></table></div>
      </section>
      <section class="team-driver-section team-driver-section--earnings">
        <div class="team-driver-earnings-heading"><div><span>Current Month</span><strong>${escapeHtml(formatMoney(profile.monthEarningsCents, locale))}</strong></div><div><span>Year to Date</span><strong>${escapeHtml(formatMoney(profile.yearEarningsCents, locale))}</strong></div></div>
        <h3>Earnings by Month</h3>
        <div class="team-driver-earnings-chart" role="img" aria-label="Driver earnings by month">
          ${profile.monthlyEarnings.map((item, index) => `<div><span>${escapeHtml(formatMoney(item.value, locale))}</span><i style="--driver-earning-height:${Math.max(4, Math.round((item.value / maxEarnings) * 100))}%"></i><small>${escapeHtml(months[index])}</small></div>`).join('')}
        </div>
      </section>
    </div>
  </section>`;
}

function employeeAction(member, role, mutationsEnabled, locale) {
  if (role !== 'admin' || !mutationsEnabled || !member.attendanceEmployeeId) {
    return '<span class="team-member-action__empty" aria-hidden="true">—</span>';
  }
  const name = escapeHtml(member.name);
  return `<div class="team-member-action">
    <button type="button" class="team-member-action__toggle" data-team-menu aria-expanded="false" aria-label="${escapeHtml(t('team.actionsFor', { name: member.name }, locale))}">
      <i class="ph ph-dots-three-vertical" aria-hidden="true"></i>
    </button>
    <div class="team-member-menu" data-team-menu-panel hidden>
      <button type="button" data-team-action="reset-password" data-employee-id="${escapeHtml(member.attendanceEmployeeId)}" data-employee-name="${name}">
        <i class="ph ph-key" aria-hidden="true"></i>${escapeHtml(t('team.resetPassword', {}, locale))}
      </button>
      <button type="button" data-team-action="toggle-status" data-employee-id="${escapeHtml(member.attendanceEmployeeId)}" data-employee-name="${name}" data-next-active="${member.attendanceActive ? 'false' : 'true'}">
        <i class="ph ${member.attendanceActive ? 'ph-user-minus' : 'ph-user-check'}" aria-hidden="true"></i>${escapeHtml(t(member.attendanceActive ? 'team.disable' : 'team.enable', {}, locale))}
      </button>
    </div>
  </div>`;
}

function memberRow(member, options) {
  const interactiveDriver = member.roleKey === 'driver' && options.driverProfiles?.has(member.id);
  const avatar = interactiveDriver
    ? `<button type="button" class="team-member-avatar team-member-avatar--${escapeHtml(member.roleTone)} team-member-avatar--interactive" data-team-driver-open="${escapeHtml(member.id)}" aria-label="${escapeHtml(`Open ${member.name} driver details`)}">${escapeHtml(member.initials)}</button>`
    : `<span class="team-member-avatar team-member-avatar--${escapeHtml(member.roleTone)}" aria-hidden="true">${escapeHtml(member.initials)}</span>`;
  return `<tr class="team-member-row" data-team-member-role="${escapeHtml(member.roleKey)}">
    <td class="team-member-cell team-member-cell--member">
      ${avatar}
      <div><strong>${escapeHtml(member.name)}</strong><small>${escapeHtml(member.department)}</small></div>
    </td>
    <td class="team-member-id" title="${escapeHtml(member.employeeId)}">${escapeHtml(member.employeeId)}</td>
    <td><span class="team-member-role team-member-role--${escapeHtml(member.roleTone)}"><i class="ph ${escapeHtml(member.roleIcon)}" aria-hidden="true"></i>${escapeHtml(member.role)}</span></td>
    <td><span class="team-member-department"><i class="ph ph-briefcase" aria-hidden="true"></i>${escapeHtml(member.department)}</span></td>
    <td><span class="team-member-status team-member-status--${escapeHtml(member.status.key)}">${escapeHtml(member.status.label)}</span></td>
    <td class="team-member-last-active">${escapeHtml(relativeLastActive(member.lastActiveAt, options.locale, options.nowMillis))}</td>
    <td class="team-member-actions-cell">${employeeAction(member, options.role, options.mutationsEnabled, options.locale)}</td>
  </tr>`;
}

function todoCompletionRankingMarkup(ranking, locale) {
  const copy = todoRankingCopy(locale);
  if (!ranking.length) {
    return `<div class="team-todo-ranking-empty" role="status">
      <i class="ph ph-list-checks" aria-hidden="true"></i>
      <strong>${escapeHtml(copy.emptyTitle)}</strong>
      <span>${escapeHtml(copy.emptyDetail)}</span>
    </div>`;
  }
  const max = Math.max(...ranking.map((item) => item.count), 1);
  return `<ol class="team-todo-ranking" data-team-todo-ranking>${ranking.map((item, index) => {
    const rank = index + 1;
    const meta = roleMeta(item.role);
    const percent = Math.max(8, Math.round((item.count / max) * 100));
    return `<li data-team-todo-worker="${escapeHtml(item.workerAccount)}" data-team-todo-role="${escapeHtml(item.role)}">
      <span class="team-todo-rank team-todo-rank--${rank}${rank > 3 ? ' team-todo-rank--plain' : ''}">${rank}</span>
      <span class="team-todo-role-icon team-todo-role-icon--${escapeHtml(meta.tone)}" aria-hidden="true"><i class="ph ${escapeHtml(meta.icon)}"></i></span>
      <div class="team-todo-ranking__body">
        <strong>${escapeHtml(item.workerAccount)}</strong>
        <small>${escapeHtml(t(meta.labelKey, {}, locale))}</small>
        <span class="team-todo-progress" aria-hidden="true"><i style="--team-todo-progress:${percent}%"></i></span>
      </div>
      <span class="team-todo-count"><strong>${item.count.toLocaleString(locale)}</strong><small>${escapeHtml(copy.completed)}</small></span>
    </li>`;
  }).join('')}</ol>`;
}

function driverRankingMarkup(ranking, locale, driverProfileIdsByName = new Map()) {
  if (!ranking.length) {
    return `<div class="team-ranking-empty"><i class="ph ph-steering-wheel" aria-hidden="true"></i><span>${escapeHtml(t('team.noDeliveriesToday', {}, locale))}</span></div>`;
  }
  const max = Math.max(...ranking.map((item) => Number(item.deliveries) || 0), 1);
  return `<ol class="team-driver-ranking">${ranking.map((item, index) => {
    const rank = index + 1;
    const percent = Math.max(8, Math.round((Number(item.deliveries || 0) / max) * 100));
    const profileId = driverProfileIdsByName.get(normalizeIdentity(item.name)) || '';
    const avatarClass = `team-driver-avatar team-driver-avatar--${rank}${profileId ? ' team-driver-avatar--interactive' : ''}`;
    const avatar = profileId
      ? `<button type="button" class="${avatarClass}" data-team-driver-open="${escapeHtml(profileId)}" aria-label="${escapeHtml(`Open ${item.name} driver details`)}">${escapeHtml(initials(item.name))}</button>`
      : `<span class="${avatarClass}" aria-hidden="true">${escapeHtml(initials(item.name))}</span>`;
    return `<li>
      <span class="team-driver-rank team-driver-rank--${rank}${rank > 3 ? ' team-driver-rank--plain' : ''}">${rank}</span>
      ${avatar}
      <div class="team-driver-rank__body">
        <strong>${escapeHtml(item.name)}</strong>
        <span class="team-driver-progress" aria-hidden="true"><i style="--team-progress:${percent}%"></i></span>
      </div>
      <span class="team-driver-deliveries"><strong>${Number(item.deliveries || 0)}</strong><small>${escapeHtml(t('team.deliveries', {}, locale))}</small></span>
    </li>`;
  }).join('')}</ol>`;
}

export function teamAttendancePageMarkup({
  overview,
  users = [],
  drivers = [],
  rows = [],
  operations,
  todoCompletionRecords = [],
  role,
  todayKey,
  locale = getLocale(),
  showRankingExamples = false,
} = {}) {
  const safe = overview ?? {};
  const timeZone = safe.office?.timezone || 'America/Los_Angeles';
  const members = buildTeamMembers({
    overview: safe,
    users,
    drivers,
    todayKey,
    timeZone,
    locale,
  });
  const nowMillis = timestampMillis(safe.serverNow) || Date.now();
  const workload = operations ?? teamOperationalSnapshot({ rows, todayKey, timeZone });
  const liveDriverRanking = Array.isArray(workload.driverRanking) ? workload.driverRanking : [];
  const rankingIsExample = showRankingExamples && liveDriverRanking.length === 0;
  const visibleDriverRanking = rankingIsExample ? DRIVER_RANKING_EXAMPLES : liveDriverRanking;
  const todoCompletionRanking = buildTodoCompletionRanking(todoCompletionRecords);
  const todoCopy = todoRankingCopy(locale);
  const canViewDriverFinance = ['admin', 'finance'].includes(String(role || '').toLowerCase());
  const driverProfiles = new Map(canViewDriverFinance
    ? drivers.map((driver) => [
      String(driver.id || ''),
      driverProfileSnapshot({ driver, rows, now: new Date(nowMillis), timeZone }),
    ])
    : []);
  const driverProfileIdsByName = new Map(
    [...driverProfiles.entries()]
      .filter(([id, profile]) => id && normalizeIdentity(profile.name))
      .map(([id, profile]) => [normalizeIdentity(profile.name), id]),
  );
  const totalEmployees = members.length;
  const activeToday = members.filter((member) => member.activeToday).length;
  const onDutyCount = members.filter((member) => member.onDuty).length;
  const onLeaveCount = members.filter((member) => member.status.key === 'on-leave').length;
  const trends = safe.trends || {};
  const labels = Array.isArray(workload.labels) && workload.labels.length === 7
    ? workload.labels
    : lastSevenDateKeys(todayKey);
  const mutationsEnabled = safe.capabilities?.mutationsEnabled === true;
  const metrics = [
    {
      kind: 'employees',
      icon: 'ph-users-three',
      title: t('team.totalEmployees', {}, locale),
      value: totalEmployees,
      series: normalizedSeries(trends.totalEmployees, totalEmployees),
      trend: metricTrend(trends.totalEmployees, trends.totalEmployeesDelta),
    },
    {
      kind: 'active',
      icon: 'ph-shield-check',
      title: t('team.activeToday', {}, locale),
      value: activeToday,
      series: normalizedSeries(trends.activeToday, activeToday),
      trend: metricTrend(trends.activeToday, trends.activeTodayDelta),
    },
    {
      kind: 'duty',
      icon: 'ph-user-focus',
      title: t('team.onDuty', {}, locale),
      value: onDutyCount,
      series: normalizedSeries(trends.onDuty, onDutyCount),
      trend: metricTrend(trends.onDuty, trends.onDutyDelta),
    },
    {
      kind: 'leave',
      icon: 'ph-calendar-check',
      title: t('team.onLeave', {}, locale),
      value: onLeaveCount,
      series: normalizedSeries(trends.onLeave, onLeaveCount),
      trend: metricTrend(trends.onLeave, trends.onLeaveDelta),
    },
  ];

  return `<section class="team-enterprise-page team-page" data-team-page>
    <h1 class="visually-hidden">${escapeHtml(t('team.title', {}, locale))}</h1>
    <section class="team-kpi-stage" aria-label="${escapeHtml(t('team.operationalSummary', {}, locale))}">
      <div class="team-kpi-grid">
        ${metrics.map((metric) => metricCard({ ...metric, labels, locale })).join('')}
      </div>
    </section>
    <div class="team-enterprise-layout">
      <section class="team-members-card" aria-labelledby="teamMembersTitle">
        <header class="team-card-heading">
          <div><h2 id="teamMembersTitle">${escapeHtml(t('team.members', {}, locale))}</h2></div>
          <strong>${escapeHtml(t('team.memberCount', { count: members.length }, locale))}</strong>
        </header>
        <div class="team-members-table-wrap">
          <table class="team-members-table" aria-labelledby="teamMembersTitle">
            <colgroup>
              <col class="team-member-column team-member-column--member">
              <col class="team-member-column team-member-column--id">
              <col class="team-member-column team-member-column--role">
              <col class="team-member-column team-member-column--department">
              <col class="team-member-column team-member-column--status">
              <col class="team-member-column team-member-column--activity">
              <col class="team-member-column team-member-column--actions">
            </colgroup>
            <tbody>${members.map((member) => memberRow(member, {
              role,
              mutationsEnabled,
              locale,
              nowMillis,
              driverProfiles,
            })).join('') || `<tr><td colspan="7" class="team-members-empty"><i class="ph ph-users-three" aria-hidden="true"></i>${escapeHtml(t('team.noMembers', {}, locale))}</td></tr>`}</tbody>
          </table>
        </div>
        <footer>${escapeHtml(t('team.showingAllMembers', { count: members.length }, locale))}</footer>
      </section>
      <aside class="team-operations-column">
        <section class="team-todo-ranking-card" aria-labelledby="teamTodoRankingTitle">
          <header><h2 id="teamTodoRankingTitle">${escapeHtml(todoCopy.title)}</h2></header>
          ${todoCompletionRankingMarkup(todoCompletionRanking, locale)}
        </section>
        <section class="team-ranking-card" aria-labelledby="teamRankingTitle">
          <header>
            <div><h2 id="teamRankingTitle">${escapeHtml(t('team.topDriverRanking', {}, locale))}</h2></div>
            ${rankingIsExample ? '<span class="team-ranking-example-badge">Example</span>' : ''}
          </header>
          ${driverRankingMarkup(visibleDriverRanking, locale, driverProfileIdsByName)}
        </section>
      </aside>
    </div>
    <div data-team-tab-host></div>
    <div data-team-driver-host></div>
    ${canViewDriverFinance ? [...driverProfiles.values()].map((profile) => `<template data-team-driver-profile="${escapeHtml(profile.id)}">${teamDriverProfileMarkup(profile, locale, { canEdit: String(role || '').toLowerCase() === 'admin' })}</template>`).join('') : ''}
  </section>`;
}

export function teamActionTabMarkup(action, dataset = {}, locale = getLocale()) {
  if (!['reset-password', 'toggle-status'].includes(action)) return '';
  const name = escapeHtml(dataset.employeeName || t('team.employee', {}, locale));
  const close = `<button type="button" class="team-action-dialog__close" data-team-tab-close aria-label="${escapeHtml(t('team.close', {}, locale))}"><i class="ph ph-x" aria-hidden="true"></i></button>`;
  const submit = `<button type="submit" class="team-action-dialog__submit"><i class="ph ph-check" aria-hidden="true"></i>${escapeHtml(t('team.done', {}, locale))}</button>`;
  if (action === 'reset-password') {
    return `<form class="team-action-dialog" data-team-action-form data-action="resetEmployeePassword" data-employee-id="${escapeHtml(dataset.employeeId)}">
      ${close}<span>${name}</span><h2>${escapeHtml(t('team.resetPassword', {}, locale))}</h2>
      <label class="system-input"><input name="password" type="password" autocomplete="new-password" minlength="10" required><span>${escapeHtml(t('team.newPassword', {}, locale))}</span></label>
      <small>${escapeHtml(t('team.oldPasswordStops', {}, locale))}</small>${submit}
    </form>`;
  }
  const enabling = dataset.nextActive === 'true';
  return `<form class="team-action-dialog" data-team-action-form data-action="setEmployeeStatus" data-employee-id="${escapeHtml(dataset.employeeId)}" data-active="${enabling}">
    ${close}<span>${name}</span><h2>${escapeHtml(t(enabling ? 'team.enableEmployee' : 'team.disableEmployee', {}, locale))}</h2>
    <p>${escapeHtml(t(enabling ? 'team.enableConfirm' : 'team.disableConfirm', {}, locale))}</p>${submit}
  </form>`;
}

export function mountTeamAttendancePage(
  root,
  {
    onMutation = async () => {},
    onDriverUpdate = async () => {},
    onReload = async () => {},
  } = {},
) {
  const page = root?.querySelector?.('[data-team-page]');
  const host = page?.querySelector?.('[data-team-tab-host]');
  const driverHost = page?.querySelector?.('[data-team-driver-host]');
  if (!page || !host || !driverHost) return Object.freeze({ destroy() {} });
  const controller = new AbortController();
  const { signal } = controller;
  const sparklineController = mountKpiSparklines(page);
  const closeDialog = () => host.replaceChildren();
  const closeDriver = () => driverHost.replaceChildren();
  const openDriver = (button) => {
    const template = page.querySelector(
      `template[data-team-driver-profile="${CSS.escape(button.dataset.teamDriverOpen || '')}"]`,
    );
    if (!template) return;
    const layer = document.createElement('div');
    layer.className = 'team-driver-drawer-layer';
    layer.dataset.teamDriverLayer = '';
    layer.append(template.content.cloneNode(true));
    driverHost.replaceChildren(layer);
    const drawer = layer.querySelector('[data-team-driver-drawer]');
    const start = button.getBoundingClientRect();
    const finish = drawer?.getBoundingClientRect();
    const motionSuppressed = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      && document.documentElement.dataset.motion !== 'on';
    if (drawer && finish && !motionSuppressed) {
      const deltaX = start.left + start.width / 2 - (finish.left + finish.width / 2);
      const deltaY = start.top + start.height / 2 - (finish.top + 70);
      drawer.animate([
        { opacity: 0, transform: `translate3d(${deltaX}px, ${deltaY}px, 0) scale(.12)` },
        { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
      ], { duration: 280, easing: 'cubic-bezier(.2,.8,.2,1)' });
    }
    drawer?.querySelector('[data-team-driver-close]')?.focus();
  };
  const closeMenus = (except = null) => {
    page.querySelectorAll('[data-team-menu]').forEach((button) => {
      const panel = button.parentElement?.querySelector('[data-team-menu-panel]');
      const keepOpen = button === except;
      button.setAttribute('aria-expanded', keepOpen ? 'true' : 'false');
      if (panel) panel.hidden = !keepOpen;
    });
  };

  page.addEventListener('click', (event) => {
    const driverButton = event.target.closest('[data-team-driver-open]');
    if (driverButton) {
      openDriver(driverButton);
      return;
    }
    const menuButton = event.target.closest('[data-team-menu]');
    if (menuButton) {
      const wasOpen = menuButton.getAttribute('aria-expanded') === 'true';
      closeMenus(wasOpen ? null : menuButton);
      return;
    }
    const actionButton = event.target.closest('[data-team-action]');
    if (actionButton) {
      closeMenus();
      host.innerHTML = teamActionTabMarkup(actionButton.dataset.teamAction, actionButton.dataset);
      host.querySelector('input')?.focus();
      return;
    }
    if (!event.target.closest('[data-team-menu-panel]')) closeMenus();
    if (event.target.closest('[data-team-tab-close]')) closeDialog();
  }, { signal });

  driverHost.addEventListener('click', (event) => {
    const editToggle = event.target.closest('[data-team-driver-edit-toggle]');
    if (editToggle) {
      const form = driverHost.querySelector('[data-team-driver-edit-form]');
      if (!form) return;
      const opening = form.hidden;
      form.hidden = !opening;
      editToggle.setAttribute('aria-expanded', opening ? 'true' : 'false');
      if (opening) form.querySelector('input')?.focus();
      return;
    }
    if (event.target.closest('[data-team-driver-edit-cancel]')) {
      const form = driverHost.querySelector('[data-team-driver-edit-form]');
      const toggle = driverHost.querySelector('[data-team-driver-edit-toggle]');
      form?.reset();
      if (form) form.hidden = true;
      toggle?.setAttribute('aria-expanded', 'false');
      return;
    }
    if (event.target.closest('[data-team-driver-close]')
      || (event.target.matches?.('[data-team-driver-layer]'))) closeDriver();
  }, { signal });

  driverHost.addEventListener('submit', async (event) => {
    event.preventDefault();
    const form = event.target.closest('[data-team-driver-edit-form]');
    if (!form || !form.reportValidity()) return;
    const submit = form.querySelector('[type="submit"]');
    const errorHost = form.querySelector('[data-team-driver-edit-error]');
    if (submit) submit.disabled = true;
    if (errorHost) errorHost.textContent = '';
    try {
      const payload = buildDriverProfileUpdatePayload(
        Object.fromEntries(new FormData(form).entries()),
        form.dataset.driverId,
      );
      await onDriverUpdate(payload);
      closeDriver();
      await onReload();
    } catch (error) {
      if (errorHost) errorHost.textContent = error?.message || 'Driver update failed.';
      if (submit) submit.disabled = false;
    }
  }, { signal });

  page.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeMenus();
    closeDialog();
    closeDriver();
  }, { signal });

  host.addEventListener('submit', async (event) => {
    event.preventDefault();
    const form = event.target.closest('[data-team-action-form]');
    if (!form || !form.reportValidity()) return;
    const submit = form.querySelector('[type="submit"]');
    submit.disabled = true;
    try {
      const action = form.dataset.action;
      const payload = action === 'resetEmployeePassword'
        ? { employeeId: form.dataset.employeeId, password: form.elements.password.value }
        : { employeeId: form.dataset.employeeId, active: form.dataset.active === 'true' };
      await onMutation(action, payload);
      closeDialog();
      await onReload();
    } catch (error) {
      form.dataset.error = error?.message || t('team.mutationError');
      submit.disabled = false;
    }
  }, { signal });

  return Object.freeze({
    destroy() {
      controller.abort();
      sparklineController.destroy();
      closeDriver();
    },
  });
}
